import { PanelDisclosure } from '../../shared/PanelDisclosure';
import { FieldSelect } from '../../shared/FieldSelect';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { MainlineEvidence } from '@services/drbfm.mainline';

export default function EvidenceEditor({ value, onChange, disabled }: {
  value: MainlineEvidence[]; onChange: (items: MainlineEvidence[]) => void; disabled: boolean;
}) {
  const { t } = useTranslation();
  const initial: MainlineEvidence = { kind: 'input_fact', title: '', excerpt: '', applicability: '' };
  const [draft, setDraft] = useState(initial);
  return <PanelDisclosure className="rounded border p-3" title={<>{t('drbfm.mainline.evidence')} ({value.length})</>}>
    <ul className="my-2 grid gap-2">{value.map((item, i) => <li key={i} className="text-sm">
      {t(`drbfm.mainline.${item.kind}`)} · {item.title} · {item.excerpt}
      {item.document_id && <a className="ml-2 underline" href={`/documents/${item.document_id}`} target="_blank" rel="noreferrer">{t('drbfm.mainline.internal_document')}</a>}
      {item.url && <a className="ml-2 underline" href={item.url} target="_blank" rel="noreferrer">{item.url}</a>}
      <span> {item.retrieved_on} {item.applicability}</span>
      <Button variant="ghost" disabled={disabled} onClick={() => onChange(value.filter((_, n) => n !== i))}>{t('drbfm.mainline.remove')}</Button>
    </li>)}</ul>
    <div className="grid gap-2 md:grid-cols-2">
      <label>{t('drbfm.mainline.sourceKind')}<FieldSelect value={draft.kind}
        onValueChange={kind => setDraft({ ...draft, kind: kind as MainlineEvidence['kind'] })} disabled={disabled}
        options={['input_fact', 'public_information', 'ai_assumption'].map(kind => ({ value: kind, label: t(`drbfm.mainline.${kind}`) }))} /></label>
      <label>{t('drbfm.mainline.sourceTitle')}<Input value={draft.title} disabled={disabled}
        onChange={e => setDraft({ ...draft, title: e.target.value })} /></label>
      {draft.kind === 'public_information' && <>
        <label>URL<Input type="url" value={draft.url ?? ''} disabled={disabled} onChange={e => setDraft({ ...draft, url: e.target.value })} /></label>
        <label>{t('drbfm.mainline.retrievedOn')}<Input type="date" value={draft.retrieved_on ?? ''} disabled={disabled}
          onChange={e => setDraft({ ...draft, retrieved_on: e.target.value })} /></label>
      </>}
      <label>{t('drbfm.mainline.quote')}<Textarea value={draft.excerpt} disabled={disabled} onChange={e => setDraft({ ...draft, excerpt: e.target.value })} /></label>
      <label>{t('drbfm.mainline.applicability')}<Textarea value={draft.applicability} disabled={disabled} onChange={e => setDraft({ ...draft, applicability: e.target.value })} /></label>
    </div>
    <Button className="mt-2" variant="outline" disabled={disabled || !draft.title.trim() || !draft.excerpt.trim()
      || (draft.kind === 'public_information' && (!draft.url || !draft.retrieved_on || !draft.applicability.trim()))}
      onClick={() => { onChange([...value, draft]); setDraft(initial); }}>{t('drbfm.mainline.addEvidence')}</Button>
    <p className="mt-2 text-sm text-muted-foreground">{t('drbfm.mainline.webHint')}</p>
  </PanelDisclosure>;
}
